import { AGENT_OR_ADMIN } from '@/shared/authorization/permissionGroups';

import { lazyRoute } from './lazy-route';
import { sectionChildren } from './section-index-redirect';

import type { RouteObject } from 'react-router-dom';

// /lrp/market/price-trends (also mounted standalone from routes.tsx)
export const lrpPriceTrends = lazyRoute(
  'price-trends',
  () => import('@/scenes/lrp/containers/LrpMarketDynamics/PriceTrends/PriceTrends'),
  { handle: { label: 'Price Trends', gate: ctx => ctx.isLiaFcs } }
);

// /lrp/market/price-evolution-analysis (also mounted standalone from routes.tsx)
export const lrpPriceEvolutionAnalysis = lazyRoute(
  'price-evolution-analysis',
  () =>
    import('@/scenes/lrp/containers/LrpMarketDynamics/PriceEvolutionAnalysis/PriceEvolutionAnalysis'),
  { handle: { label: 'Price Evolution Analysis', gate: ctx => ctx.isLiaFcs } }
);

// /lrp/market/calendar
const lrpCalendar = lazyRoute(
  'calendar',
  () => import('@/features/lrp/marketDynamics/lrpCalendar/LrpCalendar'),
  { handle: { label: 'Calendar' } }
);

// /lrp/market/alerts
const lrpAlerts = lazyRoute(
  'alerts',
  () => import('@/features/lrp/marketDynamics/alerts').then(m => ({ default: m.Alerts })),
  { handle: { label: 'Alerts' } }
);

// /lrp/market — child order mirrors the sub-nav; with the FCS-gated pair first,
// the index redirect reproduces the tenant default (price-trends for FCS, else calendar).
const lrpMarketDynamics = lazyRoute('market', () => import('@/features/lrp/marketDynamics'), {
  handle: { label: 'Market Dynamics', icon: 'NorthEast' },
  children: sectionChildren([lrpPriceTrends, lrpPriceEvolutionAnalysis, lrpCalendar, lrpAlerts])
});

// /lrp/calculator
const lrpPremiumQuoter = lazyRoute(
  'calculator',
  () => import('@/scenes/lrp/containers/LrpPremiumQuoter/LrpPremiumQuoter'),
  { handle: { label: 'Premium Quoter', icon: 'RequestPage' } }
);

// /lrp/endorsements/active-endorsements
const lrpActiveEndorsements = lazyRoute(
  'active-endorsements',
  () => import('@/scenes/lrp/containers/LrpEndorsements/ActiveEndorsementsWrapper'),
  { handle: { label: 'Active Endorsements' } }
);

// /lrp/endorsements/coverage-requests
const lrpCoverageRequests = lazyRoute(
  'coverage-requests',
  () => import('@/scenes/lrp/containers/LrpEndorsements/CoverageRequests'),
  { handle: { label: 'Coverage Requests' } }
);

// /lrp/endorsements/aip-sync — agent/admin only, mirroring the module's tab gating
const lrpAIPSync = lazyRoute(
  'aip-sync',
  () => import('@/scenes/lrp/containers/LrpEndorsements/AIPSyncWrapper'),
  { handle: { label: 'AIP Sync', gate: AGENT_OR_ADMIN } }
);

// /lrp/endorsements/coverage-overview
const coverageOverview = lazyRoute(
  'coverage-overview',
  () =>
    import('@/features/lrp/endorsements/coverage-overview').then(m => ({
      default: m.CoverageOverview
    })),
  { handle: { label: 'Coverage Overview', gate: ctx => ctx.flags.CoverageOverview } }
);

// /lrp/endorsements
const lrpEndorsements = lazyRoute(
  'endorsements',
  () => import('./lrp-endorsements-shell').then(m => ({ default: m.LrpEndorsementsShell })),
  {
    handle: { label: 'Endorsements', icon: 'AssignmentInd' },
    children: sectionChildren([
      lrpActiveEndorsements,
      lrpCoverageRequests,
      lrpAIPSync,
      coverageOverview
    ])
  }
);

// /lrp/production/{cattle,swine} — static species routes (was `:group`) so the
// sidebar tree derives them as leaves under Production.
// ponytail: both species always show, regardless of which production tag types
// the org has active — gate via the nav context if that ever matters.
const productionGroup = (group: 'cattle' | 'swine', label: string) =>
  lazyRoute(
    group,
    () =>
      import('@/features/lrp/production-groups').then(m => {
        const Element = () => <m.ProductionGroup group={group} />;
        return { default: Element };
      }),
    { handle: { label } }
  );

// /lrp/production
const lrpProduction = lazyRoute(
  'production',
  () => import('@/features/lrp/production-groups').then(m => ({ default: m.Production })),
  {
    handle: { label: 'Production', icon: 'Agriculture' },
    children: sectionChildren([
      productionGroup('cattle', 'Cattle'),
      productionGroup('swine', 'Swine')
    ])
  }
);

// /lrp/overview/preferences
const lrpQuoterPreferences = lazyRoute(
  'preferences',
  () => import('@/scenes/lrp/containers/LrpPreferences/QuoterPreferences'),
  { handle: { label: 'Quoter Preferences' } }
);

// /lrp/overview/price-report
const lrpPriceReportPreferences = lazyRoute(
  'price-report',
  () => import('@/scenes/lrp/containers/LrpPreferences/PriceReportPreferences'),
  { handle: { label: 'Price Report Pref.' } }
);

// /lrp/overview/aip
const lrpAip = lazyRoute('aip', () => import('@/containers/AIPContainer/AIPContainer'), {
  handle: { label: 'AIP' }
});

// /lrp/overview/contacts
const lrpContacts = lazyRoute(
  'contacts',
  () => import('@/scenes/lrp/components/ContactsComponent'),
  { handle: { label: 'Contacts' } }
);

// /lrp/overview/tags
const lrpTags = lazyRoute(
  'tags',
  () => import('@/features/tags/tags-management').then(m => ({ default: m.Tags })),
  { handle: { label: 'Tags' } }
);

// /lrp/overview
const lrpOperationOverview = lazyRoute(
  'overview',
  () => import('@/scenes/lrp/containers/LrpPreferences/LrpOperationOverviewModule'),
  {
    handle: { label: 'Operation Overview', icon: 'Dashboard' },
    children: sectionChildren([
      lrpQuoterPreferences,
      lrpPriceReportPreferences,
      lrpAip,
      lrpContacts,
      lrpTags
    ])
  }
);

// /lrp/cme-positions
const cmePositions = lazyRoute(
  'cme-positions',
  () => import('@/features/cme-positions').then(module => ({ default: module.CMEPositionsPage })),
  { handle: { label: 'CME Positions', icon: 'QueryStats', gate: ctx => ctx.flags.CmePositions } }
);

// /lrp
export const lrpRoutes: RouteObject = lazyRoute(
  'lrp/:name',
  () => import('@/scenes/lrp/LrpModule'),
  {
    children: [
      lrpPremiumQuoter,
      lrpEndorsements,
      cmePositions,
      lrpProduction,
      lrpOperationOverview,
      lrpMarketDynamics
    ]
  }
);
